import moment from "moment";
import type { i18n as I18nInstance } from "i18next";

/**
 * Форматтер значений для i18next-интерполяции: `{{ createdAt, dateTime }}`.
 *
 * Зеркало `site/src/shared/lib/interpolationFormat.ts` — набор имён форматов
 * должен совпадать, иначе одни и те же строки перевода будут рендериться в
 * кабинете и на сайте по-разному. В кабинете уже используется moment
 * (см. shared/components/Form/DatetimePicker.tsx), поэтому берём его же.
 */
export function interpolationFormat(
  value: string | number | Date,
  format?: string,
  lng?: string
): string {
  // Локаль ставим на инстанс, а не глобально через moment.locale(lng):
  // глобальная мутация протекала бы в несвязанный код с moment
  // (DatetimePicker и т.п.) при языке i18n, отличном от его локали.
  const localized = () => moment(value).locale(lng ?? "ru");

  if (format === "dateMonth") {
    return localized().format("DD MMM YYYY");
  }

  if (format === "onlyDate") {
    return localized().format("D MMMM YYYY");
  }

  if (format === "dateTime") {
    return localized().format("DD.MM.YYYY HH:mm");
  }

  if (format === "day") {
    return localized().format("DD");
  }

  if (format === "month") {
    return localized().format("MMMM YYYY");
  }

  if (format === "dateTimeSeconds") {
    return localized().format("DD.MM.YYYY HH:mm:ss");
  }

  if (format === "timeSeconds") {
    return localized().format("HH:mm:ss");
  }

  if (format === "year") {
    return localized().format("YYYY");
  }

  if (format === "humanize") {
    // Для humanize нужно число в секундах
    const seconds = typeof value === "number" ? value : Number(value);
    return moment
      .duration(seconds, "seconds")
      .locale(lng ?? "ru")
      .humanize(true);
  }

  if (format === "short") {
    return localized().format("DD MMMM YYYY");
  }

  if (format === "full") {
    return localized().format("DD MMMM YYYY HH:mm");
  }

  if (format === "time") {
    return localized().format("HH:mm");
  }

  if (format === "local") {
    return localized().format("LLL");
  }

  if (format === "fromNow") {
    return localized().fromNow(true); // 4 years
  }

  if (format === "fromNowAgo") {
    return localized().fromNow(); // 4 years ago
  }

  return String(value);
}

/** Имена форматов, поддерживаемых interpolationFormat (как на site). */
export const INTERPOLATION_FORMATS = [
  "dateMonth",
  "onlyDate",
  "dateTime",
  "day",
  "month",
  "dateTimeSeconds",
  "timeSeconds",
  "year",
  "humanize",
  "short",
  "full",
  "time",
  "local",
  "fromNow",
  "fromNowAgo",
] as const;

/**
 * Регистрирует форматы в i18next.
 *
 * Начиная с i18next v23 в init() встраивается штатный модуль Formatter,
 * который ПЕРЕЗАПИСЫВАЕТ `options.interpolation.format` собственной
 * реализацией (см. i18next/dist/esm/i18next.js: `this.options.interpolation
 * .format = s.formatter.format.bind(s.formatter)`), поэтому передавать свою
 * функцию через `interpolation.format` в init бессмысленно — её просто
 * затрут. Публичный путь — `i18n.services.formatter.add(name, fn)`.
 */
export function registerInterpolationFormats(i18n: I18nInstance): void {
  const formatter = i18n.services?.formatter;

  if (!formatter) {
    // services появляются синхронно внутри init(); если этот вызов случился
    // раньше — дорегистрируемся по событию
    i18n.on("initialized", () => registerInterpolationFormats(i18n));
    return;
  }

  INTERPOLATION_FORMATS.forEach((name) => {
    formatter.add(name, (value, lng) => interpolationFormat(value, name, lng));
  });
}
